//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { crl8 } from 'app/curalate.globals';
import universalImageActionsRevealV2Template from '@legacy-components/common/universal-image-actions/universal-image-actions-reveal-v2.html';

/* global ActiveXObject: false */

(function () {
  const uia = angular.module('uia');

  uia.directive('universalImageActionsRevealV2', [
    function () {
      return {
        restrict: 'E',
        scope: {},
        require: '^universalImageActionsModal',
        template: universalImageActionsRevealV2Template,
        controller: [
          '$scope',
          '$window',
          '$timeout',
          '$httpParamSerializer',
          'SyndicationService',
          'TrackingService',
          'UniversalImageActionsHelpers',
          'UniversalImageActionsItemType',
          'UniversalImageActionsApiService',
          'UniversalImageActionsService',
          function (
            $scope,
            $window,
            $timeout,
            $httpParamSerializer,
            SyndicationService,
            TrackingService,
            UniversalImageActionsHelpers,
            UniversalImageActionsItemType,
            UniversalImageActionsApiService,
            UniversalImageActionsService,
          ) {
            const launchOptions =
              UniversalImageActionsService.getCurrentLaunchOptions();
            const universalItemId = launchOptions.getUniversalItemId();
            let hasEditedShop = false;
            const SHOP_BUTTON_DEFAULT_TEXT = 'View Details';
            const BASE_PREVIEW_URL = `${crl8.session.baseUrls.reveal_v2}/`;
            const TRACKING_CATEGORY =
              UniversalImageActionsHelpers.getTrackingCategory(
                UniversalImageActionsService.getCurrentPage(),
              );
            const params = { version: 2 };

            $scope.model = {
              widgetId: null,
              embedCode: null,
              loadingEmbedCode: false,
              // Used to ensure we only ever update the embed code with the result of the last
              // request. This guarantees the order of responses is not a factor. The value is
              // arbitrary on init.
              lastEmbedCodeUpdateRequestId: 0,
              settings: {
                shopButtonText: SHOP_BUTTON_DEFAULT_TEXT,
                spatialTagIcon: 'tag',
                stretchToFit: false,
                replaceTabs: false,
                autoShowProducts: false,
              },
              defaults: {
                shopButtonText: SHOP_BUTTON_DEFAULT_TEXT,
                spatialTagIcon: 'tag',
                stretchToFit: false,
                replaceTabs: false,
                autoShowProducts: false,
              },
              currentlyInWorkflow:
                !!UniversalImageActionsService.getCurrentWorkflow(),
              outstandingAsyncAction: null,
            };

            $scope.model.close = function () {
              $scope.$emit('uia:modal:close');
            };

            $scope.model.copyToClipboardClicked = function () {
              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'Copy',
              );
            };

            // Allow the user to preview their currently configured Reveal. We'll
            // open a new window pointing to a dedicated preview page
            $scope.model.openPreviewWindow = function () {
              const previewWindowUrl = `${
                BASE_PREVIEW_URL + $scope.model.widgetId
              }/preview${$scope.getUrlParams()}`;
              $window.open(previewWindowUrl);
            };

            $scope.getUrlParams = function () {
              const embedCodeUrlParams = {};
              const settingKeys = Object.keys($scope.model.settings);

              for (
                let settingIndex = 0;
                settingIndex < settingKeys.length;
                ++settingIndex
              ) {
                const propertyName = settingKeys[settingIndex];
                if (
                  $scope.model.settings.hasOwnProperty(propertyName) &&
                  $scope.model.settings[propertyName] !==
                    $scope.model.defaults[propertyName]
                ) {
                  if ($scope.model.settings[propertyName] !== '') {
                    embedCodeUrlParams[propertyName] =
                      $scope.model.settings[propertyName];
                  }
                }
              }

              const embedCodeUrlParamsString =
                $httpParamSerializer(embedCodeUrlParams);
              return embedCodeUrlParamsString.length === 0
                ? ''
                : `?${embedCodeUrlParamsString}`;
            };

            $scope.updateEmbedCode = function () {
              const requestId = Date.now();
              $scope.model.lastEmbedCodeUpdateRequestId = requestId;
              // This needs to call the underlying API with the correct parameters and get a new widget code
              // the UI also should potentially have an indication that the embed code is changing
              // $scope.model.outstandingAsyncAction = true;
              $timeout(() => {
                $scope.model.loadingEmbedCode = true;
                $scope.model.hasCopiedLink = false;
              });

              $scope.revealEmbedCodeLoad(requestId);
            };

            $scope.updateShopButtonTextSetting = function () {
              $scope.updateEmbedCode();
              if (!hasEditedShop) {
                TrackingService.recordEvent(
                  TRACKING_CATEGORY,
                  TrackingService.ACTION_REVEAL,
                  'CTA',
                );
                hasEditedShop = true;
              }
            };

            $scope.updateStretchToFitSetting = function () {
              $scope.updateEmbedCode();
              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'Fill',
              );
            };

            $scope.updateReplaceTabs = function () {
              $scope.updateEmbedCode();
              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'Replace',
              );
            };

            $scope.updateSpatialTagIcon = function (spatialTagIcon) {
              $scope.model.settings.spatialTagIcon = spatialTagIcon;
              $scope.updateEmbedCode();
              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'Tag',
              );
            };

            $scope.updateAutoShowProducts = function () {
              $scope.updateEmbedCode();
              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'AutoShow',
              );
            };

            $scope.processRevealEmbedCodeLoad = function (result) {
              if (
                launchOptions.getItemType() ===
                UniversalImageActionsItemType.syndication
              ) {
                SyndicationService.saveUserImageActivity(
                  launchOptions.getItemId(),
                  'reveal',
                );
              }

              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_REVEAL,
                'GenerateEmbedCode',
              );

              $timeout(() => {
                $scope.model.widgetId = result.widgetId;
                $scope.model.embedCode = result.embedCode.trim();
              });
            };

            $scope.revealEmbedCodeLoad = function (requestId) {
              UniversalImageActionsApiService.getRevealEmbedCodeAsync(
                universalItemId,
                $.extend({}, params, $scope.model.settings),
              )
                .then((r) => {
                  if (requestId === $scope.model.lastEmbedCodeUpdateRequestId) {
                    $scope.processRevealEmbedCodeLoad(r);
                  }
                })
                .finally(() => {
                  if (requestId === $scope.model.lastEmbedCodeUpdateRequestId) {
                    $scope.model.outstandingAsyncAction = null;
                    $scope.model.loadingEmbedCode = false;
                  }
                });
            };

            //
            // Initialization
            //
            $scope.model.outstandingAsyncAction = 'getEmbedCode';
            params.overrideImageUrl =
              UniversalImageActionsService.getCanonicalImageUrl();

            $scope.$on('copy-button:copied', (e) => {
              $scope.model.hasCopiedLink = true;
              e.stopPropagation();
            });

            $scope.revealEmbedCodeLoad(
              $scope.model.lastEmbedCodeUpdateRequestId,
            );

            UniversalImageActionsService.trackMediaEvent(
              MediaTrackingEventName.RevealCreated,
            );
          },
        ],
        link(scope, element, attrs, universalImageActionsModalCtrl) {
          scope.model.currentlyInWorkflow =
            universalImageActionsModalCtrl.currentlyInWorkflow();

          // When the user clicks on the embed code text in the modal,
          // we'll automatically select all of the text for them. Note
          // that we unbind mouseup here because Chrome will deselect
          // text on mouseup after the focus event fires.
          angular.element('.widget-embed-code-container').focus(function () {
            const elem = angular.element(this);
            elem.select();
            elem.mouseup(() => {
              elem.unbind('mouseup');
              return false;
            });
          });
        },
      };
    },
  ]);
})();
